import { useMemo } from "react";

export default function TournamentParticipationTable({ results, tournaments }) {
  const tournamentData = useMemo(() => {
    const merged = results
      .map((r) => {
        const t = tournaments.find((tour) => tour.id === r.tournamentId);
        if (!t) return null;

        const startDate = t.startDate?.toDate ? t.startDate.toDate() : new Date(t.startDate);
        const endDate = t.endDate?.toDate ? t.endDate.toDate() : new Date(t.endDate);

        // priority: rank > rankNote > placement
        const ranking =
          r.rank ??
          (r.rankNote && r.rankNote.trim() !== "" ? r.rankNote : r.placement ?? "-");

        // color by rankingType
        let rowColor = "bg-gray-50"; // default
        if (r.rankingType === "AJSS") rowColor = "bg-blue-50";
        else if (r.rankingType === "National") rowColor = "bg-green-50";

        return {
          id: t.id,
          name: t.name,
          startDate,
          endDate,
          rankingType: r.rankingType,
          date:
            startDate.getTime() === endDate.getTime()
              ? startDate.toLocaleDateString("en-GB")
              : `${startDate.toLocaleDateString("en-GB")} - ${endDate.toLocaleDateString("en-GB")}`,
          ranking,
          rowColor,
        };
      })
      .filter(Boolean);

    // 🔑 sort by startDate descending (latest on top)
    return merged.sort((a, b) => b.startDate - a.startDate);
  }, [results, tournaments]);

  // Collect unique types from data
  const rankingTypesPresent = [...new Set(tournamentData.map((t) => t.rankingType))];

  if (!tournamentData.length) {
    return <p className="text-gray-500">No tournaments participated.</p>;
  }

  return (
    <div className="p-4 bg-white shadow rounded-xl overflow-x-auto">
      <h1 className="mb-4">Tournament Participation</h1>

      {/* Dynamic Legend with if/else */}
      <div className="flex items-center space-x-4 mb-2 text-sm">
        {rankingTypesPresent.map((type) => {
          if (type === "AJSS") {
            return (
              <div key={type} className="flex items-center space-x-1">
                <span className="w-4 h-4 bg-blue-50 border border-blue-300 rounded"></span>
                <span>AJSS</span>
              </div>
            );
          } else if (type === "National") {
            return (
              <div key={type} className="flex items-center space-x-1">
                <span className="w-4 h-4 bg-green-50 border border-green-300 rounded"></span>
                <span>National</span>
              </div>
            );
          } else {
            return (
              <div key={type} className="flex items-center space-x-1">
                <span className="w-4 h-4 bg-gray-50 border border-gray-300 rounded"></span>
                <span>Other</span>
              </div>
            );
          }
        })}
      </div>

      <p className="mb-2 font-medium">
        Total Tournaments Participated: {tournamentData.length}
      </p>
      <table className="w-full border-collapse border border-gray-300 text-sm">
        <thead className="bg-gray-100">
          <tr>
            <th className="border border-gray-300 p-2 text-left">Tournament</th>
            <th className="border border-gray-300 p-2">Date</th>
            <th className="border border-gray-300 p-2">Ranking</th>
          </tr>
        </thead>
        <tbody>
          {tournamentData.map((t) => (
            <tr key={t.id} className={t.rowColor}>
              <td className="border border-gray-300 p-2 text-left">{t.name}</td>
              <td className="border border-gray-300 p-2">{t.date}</td>
              <td className="border border-gray-300 p-2 font-medium">{t.ranking}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
